/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { showMessage } from '../store/BASE/messageSlice';
// instance models
import ProjectModel from '../main/projects/model/ProjectModel';
import CourseModel from '../main/courses/model/CourseModel';
import LessonModel from '../main/lessons/model/LessonModel';
import SectionModel from '../main/sections/model/SectionModel';
import PageModel from '../main/pages/model/PageModel';
import CommentModel from '../main/comments/model/CommentModel';
import TemplateModel from '../main/templates/model/TemplateModel';
import UserModel from '../main/users/model/UserModel';
import OrganizationModel from '../main/organizations/model/OrganizationModel';
import instance from '../auth/services/jwtService';

export const singleResource = (resource) => {
  // replace underscores with spaces
  // remove trailing 's' from resource name
  const str = resource.replace(/_/g, ' ').slice(0, -1);
  // captialize the first letter of each word in the resource name
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

// GET requests
export const getListing = (resource) =>
  createAsyncThunk(
    `${resource}/get${singleResource(resource)}s`,
    async (params, { dispatch }) => {
      try {
        // send request to get projects
        const response = await axios.get(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/${resource}`,
          params
        );
        // get data from response
        return response.data;
      } catch (error) {
        // show error message
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

export const getInstance = (resource) =>
  createAsyncThunk(
    `${resource}/get${singleResource(resource)}`,
    async (id, { dispatch }) => {
      try {
        // send request to get project
        const response = await axios.get(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/${resource}/${id}`
        );
        // get data from response
        return response.data;
      } catch (error) {
        // show error message
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

// Define your mappings
const templateMap = {
  'TMP-L-007': {
    source: 'section',
    templates: ['TMP-L-005', 'TMP-L-006']
  },
  'TMP-L-008': {
    source: 'section',
    templates: ['TMP-L-005', 'TMP-L-006']
  },
  'TMP-L-009': {
    source: 'section',
    templates: ['TMP-L-005', 'TMP-L-006']
  },
  'TMP-L-010': {
    source: 'section',
    templates: ['TMP-L-005', 'TMP-L-006']
  },
  'TMP-L-011': {
    source: 'section',
    templates: ['TMP-L-005', 'TMP-L-006']
  },
  'TMP-L-012': {
    source: 'section',
    templates: ['TMP-L-005', 'TMP-L-006']
  },
  'TMP-L-013': {
    source: 'section',
    templates: [
      'TMP-L-007',
      'TMP-L-008',
      'TMP-L-009',
      'TMP-L-010',
      'TMP-L-011',
      'TMP-L-012'
    ]
  },
  'TMP-PE-004': {
    source: 'section',
    templates: [
      'TMP-PE-007',
      'TMP-PE-008',
      'TMP-PE-009',
      'TMP-PE-010',
      'TMP-PE-011',
      'TMP-PE-012',
      'TMP-PE-013',
      'TMP-PE-014',
      'TMP-PE-015',
      'TMP-PE-016',
      'TMP-PE-017',
      'TMP-PE-018',
      'TMP-PE-019',
      'TMP-PE-020',
      'TMP-PE-021',
      'TMP-PE-022',
      'TMP-PE-023',
      'TMP-PE-024',
      'TMP-PE-025',
      'TMP-PE-026',
      'TMP-PE-027',
      'TMP-PE-028'
    ]
  },
  'TMP-PE-005': {
    source: 'section',
    templates: ['TMP-PE-004', 'TMP-PE-006', 'TMP-PE-029']
  },
  'TMP-PE-006': {
    source: 'section',
    templates: [
      'TMP-PE-007',
      'TMP-PE-008',
      'TMP-PE-009',
      'TMP-PE-010',
      'TMP-PE-011',
      'TMP-PE-012',
      'TMP-PE-013',
      'TMP-PE-014',
      'TMP-PE-015',
      'TMP-PE-016',
      'TMP-PE-017',
      'TMP-PE-018',
      'TMP-PE-019',
      'TMP-PE-020',
      'TMP-PE-021',
      'TMP-PE-022',
      'TMP-PE-023',
      'TMP-PE-024',
      'TMP-PE-025',
      'TMP-PE-026',
      'TMP-PE-027',
      'TMP-PE-028'
    ]
  },
  'TMP-PE-011': {
    dynamicSource: true,
    templates: ['TMP-L-005', 'TMP-L-006', 'TMP-PE-007', 'TMP-PE-008']
  },
  'TMP-PE-012': {
    dynamicSource: true,
    templates: ['TMP-L-005', 'TMP-L-006', 'TMP-PE-007', 'TMP-PE-008']
  },
  'TMP-PE-029': {
    source: 'section',
    templates: ['TMP-L-020']
  },
  'TMP-PE-030': {
    source: 'section',
    templates: [
      'TMP-PE-004',
      'TMP-PE-005',
      'TMP-PE-006',
      'TMP-PE-011',
      'TMP-PE-012',
      'TMP-PE-017',
      'TMP-PE-018',
      'TMP-PE-019',
      'TMP-PE-020',
      'TMP-PE-029'
    ]
  },
  'TMP-A-002': {
    multipleSources: true,
    source: 'lesson',
    templates: ['TMP-L-005', 'TMP-L-006', 'TMP-PE-007', 'TMP-PE-008']
  },
  'TMP-A-003': {
    multipleSources: true,
    source: 'lesson',
    templates: ['TMP-L-005', 'TMP-L-006', 'TMP-PE-007', 'TMP-PE-008']
  },
  'TMP-PE-015': {
    source: 'section',
    templates: [
      'TMP-PE-007',
      'TMP-PE-008',
      'TMP-PE-009',
      'TMP-PE-010',
      'TMP-PE-011',
      'TMP-PE-012',
      'TMP-PE-013',
      'TMP-PE-014',
      'TMP-PE-015',
      'TMP-PE-016',
      'TMP-PE-017',
      'TMP-PE-018',
      'TMP-PE-019',
      'TMP-PE-020',
      'TMP-PE-021',
      'TMP-PE-022',
      'TMP-PE-023',
      'TMP-PE-024',
      'TMP-PE-025',
      'TMP-PE-026',
      'TMP-PE-027',
      'TMP-PE-028'
    ]
  }
};

export const fetchPossiblePages = createAsyncThunk(
  'pages/fetchPossiblePages',
  async (data, { dispatch }) => {
    console.warn('got data: ', data);
    let possiblePages = null;
    const templateLabel = data?.template?.label;

    const mapping = templateMap[templateLabel];

    if (!mapping) {
      console.error(
        `No page links fetch configured for template: ${templateLabel}`
      );
      return null;
    }

    const { source, templates, dynamicSource, multipleSources } = mapping;

    let targetId = null;
    let targetIdArray = null;
    let targetType = null;

    if (dynamicSource) {
      const isFinal = data?.section?.classification
        ?.toLowerCase()
        .includes('final');
      targetId = isFinal ? data?.section?.lesson?.id : data?.section?.id;
      targetType = isFinal ? 'lesson' : 'section';
    } else if (multipleSources) {
      targetType = 'lesson';
      const linkedArray = data?.section?.lesson?.linked_lesson_list;
      const linkedSingle = data?.section?.lesson?.linked_lesson;

      targetIdArray = Array.isArray(linkedArray)
        ? linkedArray
        : linkedSingle
        ? [linkedSingle]
        : null;
    } else {
      if (source === 'section') {
        targetId = data?.section?.id;
        targetType = 'section';
      } else if (source === 'lesson') {
        targetId = data?.section?.lesson?.id;
        targetType = 'lesson';
      }
    }

    if ((targetId || targetIdArray) && targetType) {
      const apiStart = performance.now();

      try {
        if (!targetIdArray) {
          const result = await dispatch(
            getPageLinks(targetType)({
              id: targetId,
              templates: templates
            })
          ).unwrap();
          possiblePages = result;
        } else {
          const result = await dispatch(
            getPageLinksMultipleLessons({
              ids: targetIdArray,
              templates: templates
            })
          ).unwrap();
          possiblePages = result;
        }
      } catch (error) {
        console.error('Error fetching page links:', error);
      }

      const apiEnd = performance.now();
    }

    return possiblePages;
  }
);

export const getEditInstance = (resource) =>
  createAsyncThunk(
    `${resource}/getEdit${singleResource(resource)}`,
    async (id, { dispatch }) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/${resource}/${id}`
        );
        const data = response.data;

        // Normal return for other resources
        return data;
      } catch (error) {
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

export const getPageLinks = (resource) =>
  createAsyncThunk(
    `pagelinks/${resource}`,
    async ({ id, templates }, { dispatch }) => {
      try {
        const params = new URLSearchParams();

        if (Array.isArray(templates) && templates.length > 0) {
          templates.forEach((id) => params.append('templates', id));
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/page-links/${resource}/${id}`,
          { params }
        );

        return response.data;
      } catch (error) {
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

export const getPageLinksMultipleLessons = createAsyncThunk(
  `pagelinks/lessons`,
  async ({ ids, templates }, { dispatch }) => {
    try {
      const params = new URLSearchParams();
      ids.forEach((id) => params.append('ids[]', id));

      if (Array.isArray(templates) && templates.length > 0) {
        templates.forEach((id) => params.append('templates', id));
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/page-links/lessons`,
        { params }
      );

      return response.data;
    } catch (error) {
      dispatch(showMessage({ message: error.message, variant: 'error' }));
      throw error;
    }
  }
);

export const getInstanceComments = (resource) =>
  createAsyncThunk(
    `${resource}/get${singleResource(resource)}Comments`,
    async (
      {
        id,
        numberOfComments,
        commentPage,
        searchText,
        showApproved,
        showInternal,
        reviewPeriods
      },
      { dispatch }
    ) => {
      try {
        const params = new URLSearchParams();
        params.append('numberOfComments', numberOfComments);
        params.append('commentPage', commentPage);
        params.append('searchText', searchText);
        params.append('showApproved', showApproved);
        params.append('showInternal', showInternal);

        if (Array.isArray(reviewPeriods) && reviewPeriods.length > 0) {
          reviewPeriods.forEach((id) => params.append('reviewPeriods[]', id));
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/${resource}/${id}/comments`,
          { params }
        );

        return response.data;
      } catch (error) {
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

export const getInstanceIMIComments = (resource) =>
  createAsyncThunk(
    `${resource}/get${singleResource(resource)}IMIComments`,
    async (
      {
        id,
        numberOfComments,
        commentPage,
        searchText,
        showApproved,
        showInternal,
        reviewPeriods
      },
      { dispatch }
    ) => {
      try {
        const params = new URLSearchParams();
        params.append('numberOfComments', numberOfComments);
        params.append('commentPage', commentPage);
        params.append('searchText', searchText);
        params.append('showApproved', showApproved);
        params.append('showInternal', showInternal);

        if (Array.isArray(reviewPeriods) && reviewPeriods.length > 0) {
          reviewPeriods.forEach((id) => params.append('reviewPeriods[]', id));
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/${resource}/${id}/imi_comments`,
          { params }
        );

        return response.data;
      } catch (error) {
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

export const getInstancePreTrialComments = (resource) =>
  createAsyncThunk(
    `${resource}/get${singleResource(resource)}PreTrialComments`,
    async (
      {
        id,
        numberOfComments,
        commentPage,
        searchText,
        showApproved,
        showInternal,
        reviewPeriods
      },
      { dispatch }
    ) => {
      try {
        const params = new URLSearchParams();
        params.append('numberOfComments', numberOfComments);
        params.append('commentPage', commentPage);
        params.append('searchText', searchText);
        params.append('showApproved', showApproved);
        params.append('showInternal', showInternal);

        if (Array.isArray(reviewPeriods) && reviewPeriods.length > 0) {
          reviewPeriods.forEach((id) => params.append('reviewPeriods[]', id));
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/${resource}/${id}/pre_trial_comments`,
          { params }
        );

        return response.data;
      } catch (error) {
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

export const getInstanceCommentsCounts = (resource) =>
  createAsyncThunk(
    `${resource}/get${singleResource(resource)}CommentsCounts`,
    async ({ id, showInternal }, { dispatch }) => {
      try {
        // send request to get project
        const response = await axios.get(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/${resource}/${id}/comment_summary`,
          {
            params: {
              showInternal
            }
          }
        );
        // get data from response
        return response.data;
      } catch (error) {
        // show error message
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

export const getInstanceIMICommentsCounts = (resource) =>
  createAsyncThunk(
    `${resource}/get${singleResource(resource)}IMICommentsCounts`,
    async ({ id, showInternal }, { dispatch }) => {
      try {
        // send request to get project
        const response = await axios.get(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/${resource}/${id}/imi_comment_summary`,
          {
            params: {
              showInternal
            }
          }
        );
        // get data from response
        return response.data;
      } catch (error) {
        // show error message
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

export const getInstancePreTrialCommentsCounts = (resource) =>
  createAsyncThunk(
    `${resource}/get${singleResource(resource)}PreTrialCommentsCounts`,
    async ({ id, showInternal }, { dispatch }) => {
      try {
        // send request to get project
        const response = await axios.get(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/${resource}/${id}/pre_trial_comment_summary`,
          {
            params: {
              showInternal
            }
          }
        );
        // get data from response
        return response.data;
      } catch (error) {
        // show error message
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

export const getLessonPages = createAsyncThunk(
  'lessons/getLessonPages',
  async (id, { dispatch }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/lessons/${id}/pages`
      );
      return response.data;
    } catch (error) {
      dispatch(showMessage({ message: error.message, variant: 'error' }));
      throw error;
    }
  }
);

export const getRelatedInstances = (resource) =>
  createAsyncThunk(
    `${resource}/getRelated${singleResource(resource)}`,
    async (id, { dispatch }) => {
      try {
        // send request to get related projects
        const response = await axios.get(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/${resource}/${id}`
        );
        // get data from response
        return response.data;
      } catch (error) {
        // show error message
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

// not sure if we would ever need this outside a lesson but why not
export const getInstanceExport = (resource) =>
  createAsyncThunk(
    `${resource}/get${singleResource(resource)}Export`,
    async (id, { dispatch }) => {
      try {
        // send request to get project export
        const response = await axios.get(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/${resource}/${id}/pages`
        );
        // get data from response
        return response.data;
      } catch (error) {
        // show error message
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

// instance models
const models = {
  projects: ProjectModel,
  courses: CourseModel,
  lessons: LessonModel,
  sections: SectionModel,
  pages: PageModel,
  comments: CommentModel,
  imi_comments: CommentModel,
  pre_trial_comments: CommentModel,
  templates: TemplateModel,
  users: UserModel,
  organizations: OrganizationModel
};

export const newInstance = (resource) =>
  createAsyncThunk(
    `${resource}/new${singleResource(resource)}`,
    async (params, { dispatch }) => {
      try {
        // return new project model
        return models[resource]() || null;
      } catch (error) {
        // show error message
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

// POST requests
export const addInstance = (resource) =>
  createAsyncThunk(
    `${resource}/add${singleResource(resource)}`,
    async (item, { dispatch }) => {
      try {
        // send request to add project
        const response = await axios.post(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/${resource}`,
          item
        );
        // show success message
        dispatch(showMessage({ message: `${singleResource(resource)} Added` }));
        // get data from response
        return response.data;
      } catch (error) {
        // show error message
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

export const addLessonVersion = createAsyncThunk(
  'lessons/addLessonVersion',
  async ({ lessonId, data }, { dispatch }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/lessons/${lessonId}/archive`,
        data,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      dispatch(showMessage({ message: 'Lesson version archived' }));
      return response.data;
    } catch (error) {
      //   dispatch(showMessage({ message: error.message, variant: 'error' }));
      if (error.response) {
        // Log everything useful
        console.error('Archive Error:', {
          status: error.response.status,
          error: error.response.data?.error,
          message: error.message
        });

        // Show the cleanest possible error in the UI
        dispatch(
          showMessage({
            message: error.response.data?.error || 'Archiving lesson failed',
            variant: 'error'
          })
        );
      } else if (error.request) {
        console.error('No response received:', error.request);
        dispatch(
          showMessage({
            message: 'No response received from server',
            variant: 'error'
          })
        );
      } else {
        console.error('Error setting up request:', error.message);
        dispatch(showMessage({ message: error.message, variant: 'error' }));
      }

      throw error;
    }
  }
);

// PATCH requests
export const updateInstance = (resource) =>
  createAsyncThunk(
    `${resource}/update${singleResource(resource)}`,
    async (instance, { dispatch }) => {
      try {
        // send request to update project
        const response = await axios.patch(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/${resource}/${instance.id}`,
          instance
        );
        // show success message
        dispatch(
          showMessage({ message: `${singleResource(resource)} Updated` })
        );
        const data = response.data;

        // get data from response
        return data;
      } catch (error) {
        if (error.response) {
          // Display error message from the server
          dispatch(
            showMessage({
              message: error.response.data.message || 'Update failed',
              variant: 'error'
            })
          );
        } else if (error.request) {
          // Log request details in case the server didn't respond
          dispatch(
            showMessage({
              message: 'No response received from server',
              variant: 'error'
            })
          );
        } else {
          // Log other types of errors
          dispatch(showMessage({ message: error.message, variant: 'error' }));
        }

        throw error;
      }
    }
  );

// DELETE requests
export const deleteInstance = (resource) =>
  createAsyncThunk(
    `${resource}/delete${singleResource(resource)}`,
    async (instance, { dispatch }) => {
      try {
        // send request to delete project
        await axios.delete(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/${resource}/${instance.id}`,
          { data: instance.data }
        );
        // show success message
        dispatch(
          showMessage({ message: `${singleResource(resource)} Deleted` })
        );
        return instance.id;
      } catch (error) {
        // show error message
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

// Update multiple comments
export const updateMultipleComments = (resource) =>
  createAsyncThunk(
    `${resource}/updateMultipleComments`,
    async (item, { dispatch }) => {
      try {
        // send requeest to update all comments
        const response = await axios.post(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/comments/batch-approve`,
          item
        );
        // show success message
        dispatch(
          showMessage({ message: 'Mutiple comments updated successfully' })
        );
        // get data from response
        return response.data;
      } catch (error) {
        // show error message
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

export const updateMultipleIMIComments = (resource) =>
  createAsyncThunk(
    `${resource}/updateMultipleIMIComments`,
    async (item, { dispatch }) => {
      try {
        // send requeest to update all comments
        const response = await axios.post(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/imi_comments/batch-approve`,
          item
        );
        // show success message
        dispatch(
          showMessage({ message: 'Mutiple comments updated successfully' })
        );
        // get data from response
        return response.data;
      } catch (error) {
        // show error message
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

export const updateMultiplePreTrialComments = (resource) =>
  createAsyncThunk(
    `${resource}/updateMultiplePreTrialComments`,
    async (item, { dispatch }) => {
      try {
        // send requeest to update all comments
        const response = await axios.post(
          `${process.env.REACT_APP_API_SERVICE_URL}/api/pre_trial_comments/batch-approve`,
          item
        );
        // show success message
        dispatch(
          showMessage({ message: 'Mutiple comments updated successfully' })
        );
        // get data from response
        return response.data;
      } catch (error) {
        // show error message
        dispatch(showMessage({ message: error.message, variant: 'error' }));
        throw error;
      }
    }
  );

// export const handleInstanceAction = async (action, resource) => {
//   try {
//     let response;
//     response = await dispatch(action).unwrap();
//     // redirect to listing
//     useNavigate(`/${resource}/${response.id}`);
//   } catch (error) {
//     // show error message
//     dispatch(showMessage({ message: error.message, variant: "error" }));
//     throw error;
//   }
// };
